import { useState, useRef } from 'react'
import { useUploadFotoPerfil, useRemoverFotoPerfil } from '../hooks/useAuth'
import { Camera, Upload, X, Loader2, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import Cropper from 'react-easy-crop'
import type { Area, Point } from 'react-easy-crop'
import 'react-easy-crop/react-easy-crop.css'

interface UploadFotoPerfilProps {
  userId: string
  fotoPerfilAtual?: string
  onUploadSuccess?: (fotoUrl: string | null) => void
  className?: string
}

const UploadFotoPerfil = ({ 
  userId, 
  fotoPerfilAtual, 
  onUploadSuccess,
  className = '' 
}: UploadFotoPerfilProps) => {
  const [preview, setPreview] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showCropModal, setShowCropModal] = useState(false)
  const [imageToCrop, setImageToCrop] = useState<string | null>(null)
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const originalFileRef = useRef<File | null>(null)
  
  const uploadMutation = useUploadFotoPerfil()
  const removerMutation = useRemoverFotoPerfil()

  const getFotoUrl = () => {
    if (preview) return preview
    if (fotoPerfilAtual) {
      // Se já contém o domínio completo, usar diretamente
      if (fotoPerfilAtual.startsWith('http://') || fotoPerfilAtual.startsWith('https://')) {
        return fotoPerfilAtual
      }
      // Se é uma URL relativa, construir a URL completa
      const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8888/api'
      
      // Se a URL começa com /api, remover para evitar duplicação
      let relativePath = fotoPerfilAtual
      if (relativePath.startsWith('/api/')) {
        relativePath = relativePath.substring(4) // Remove '/api' mas mantém a '/'
      } else if (!relativePath.startsWith('/')) {
        relativePath = `/${relativePath}`
      }
      
      return `${apiUrl}${relativePath}`
    }
    return null
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
    if (!allowedTypes.includes(file.type)) {
      setError('Tipo de arquivo não permitido. Use apenas: JPG, JPEG, PNG, WEBP ou GIF')
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    // Validar tamanho (5MB)
    const maxSize = 5 * 1024 * 1024 // 5MB em bytes
    if (file.size > maxSize) {
      setError('Arquivo muito grande. Tamanho máximo: 5MB')
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    // Salvar arquivo original
    originalFileRef.current = file
    
    // Preview da imagem para crop
    const reader = new FileReader()
    reader.onloadend = () => {
      const imageUrl = reader.result as string
      
      // Verificar se a imagem foi carregada corretamente
      const img = new Image()
      img.crossOrigin = 'anonymous'
      
      img.onload = () => {
        // Garantir que a imagem esteja definida antes de abrir o modal
        setImageToCrop(imageUrl)
        // Aguardar um tick para garantir que o estado foi atualizado
        setTimeout(() => {
          setShowCropModal(true)
          setPreview(null)
          // Resetar zoom e crop para posição inicial
          setZoom(1)
          setCrop({ x: 0, y: 0 })
          setCroppedAreaPixels(null)
        }, 100)
      }
      
      img.onerror = () => {
        setError('Erro ao carregar a imagem. Tente novamente.')
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        originalFileRef.current = null
      }
      
      // Definir src após configurar os handlers
      img.src = imageUrl
    }
    
    reader.onerror = () => {
      setError('Erro ao ler o arquivo. Tente novamente.')
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      originalFileRef.current = null
    }
    
    reader.readAsDataURL(file)
    setError(null)
  }

  // Função para criar imagem a partir do canvas
  const createImage = (url: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const image = new Image()
      image.addEventListener('load', () => resolve(image))
      image.addEventListener('error', (error) => reject(error))
      image.src = url
    })
  }

  // Função para obter a imagem cortada
  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: Area
  ): Promise<Blob> => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      throw new Error('Não foi possível criar o contexto do canvas')
    }

    // Definir tamanho do canvas igual à área cortada
    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height

    // Desenhar a imagem cortada no canvas
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    )

    // Converter canvas para Blob
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas está vazio'))
          return
        }
        resolve(blob)
      }, 'image/jpeg', 0.95) // Qualidade 95%
    })
  }

  // Função para aplicar o crop
  const handleCropComplete = async () => {
    if (!imageToCrop || !croppedAreaPixels) {
      toast.error('Por favor, ajuste a imagem antes de aplicar')
      return
    }

    try {
      // Criar a imagem cortada
      const croppedBlob = await getCroppedImg(imageToCrop, croppedAreaPixels)
      
      // Criar um File a partir do Blob
      const croppedFile = new File(
        [croppedBlob],
        originalFileRef.current?.name || 'foto-perfil.jpg',
        { type: 'image/jpeg' }
      )

      // Criar preview da imagem cortada
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreview(reader.result as string)
      }
      reader.readAsDataURL(croppedFile)

      // Fechar modal de crop
      setShowCropModal(false)
      setImageToCrop(null)
      
      // Atualizar o arquivo no input (criar um novo FileList seria complexo, então vamos armazenar o arquivo cortado)
      originalFileRef.current = croppedFile
    } catch (error) {
      toast.error('Erro ao processar a imagem. Tente novamente.')
    }
  }

  // Função para cancelar crop
  const handleCancelCrop = () => {
    setShowCropModal(false)
    setImageToCrop(null)
    setPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    originalFileRef.current = null
  }

  const handleUpload = async () => {
    // Usar o arquivo cortado se disponível, senão usar o arquivo original
    const file = originalFileRef.current || fileInputRef.current?.files?.[0]
    
    if (!file) {
      setError('Selecione uma imagem')
      return
    }

    try {
      const response = await uploadMutation.mutateAsync({ id: userId, file })
      
      if (onUploadSuccess) {
        onUploadSuccess(response.fotoPerfil)
      }
      
      // Limpar preview e input
      setPreview(null)
      setImageToCrop(null)
      originalFileRef.current = null
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      setError(null)
    } catch (error) {
      // Erro já tratado no hook
      setError(null)
    }
  }

  const handleRemove = async () => {
    if (!fotoPerfilAtual) {
      toast.error('Usuário não possui foto de perfil')
      return
    }

    if (!window.confirm('Tem certeza que deseja remover sua foto de perfil?')) {
      return
    }

    try {
      await removerMutation.mutateAsync(userId)
      
      if (onUploadSuccess) {
        onUploadSuccess(null)
      }
      
      setPreview(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      setError(null)
    } catch (error) {
      // Erro já tratado no hook
    }
  }

  const fotoUrl = getFotoUrl()
  const isLoading = uploadMutation.isLoading || removerMutation.isLoading

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Preview/Avatar */}
      <div className="flex justify-center">
        <div className="relative">
          {fotoUrl ? (
            <img
              src={fotoUrl}
              alt="Foto de perfil"
              className="h-32 w-32 rounded-full object-cover border-4 border-gray-200"
              onError={(e) => {
                // Se a imagem não carregar, usar placeholder
                e.currentTarget.style.display = 'none'
                e.currentTarget.nextElementSibling?.classList.remove('hidden')
              }}
            />
          ) : null}
          <div className={`h-32 w-32 rounded-full bg-primary-100 flex items-center justify-center border-4 border-gray-200 ${fotoUrl ? 'hidden' : ''}`}>
            <Camera className="h-12 w-12 text-primary-600" />
          </div>
          
          {/* Overlay para upload quando hover */}
          {!isLoading && (
            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-50 rounded-full flex items-center justify-center transition-all cursor-pointer group"
              onClick={() => fileInputRef.current?.click()}>
              <Camera className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          )}
        </div>
      </div>

      {/* Input de arquivo (oculto) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
        onChange={handleFileChange}
        className="hidden"
        disabled={isLoading}
      />

      {/* Mensagem de erro */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Botões de ação */}
      <div className="flex flex-col gap-2">
        {preview && (
          <div className="flex gap-2">
            <button
              onClick={handleUpload}
              disabled={isLoading}
              className="flex-1 btn btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Enviar Foto
                </>
              )}
            </button>
            <button
              onClick={() => {
                setPreview(null)
                if (fileInputRef.current) {
                  fileInputRef.current.value = ''
                }
                setError(null)
              }}
              disabled={isLoading}
              className="btn btn-secondary flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        
        {!preview && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            className="w-full btn btn-secondary flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <Camera className="h-4 w-4" />
                {fotoPerfilAtual ? 'Alterar Foto' : 'Adicionar Foto'}
              </>
            )}
          </button>
        )}

        {fotoPerfilAtual && !preview && (
          <button
            onClick={handleRemove}
            disabled={isLoading}
            className="w-full btn btn-danger flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Removendo...
              </>
            ) : (
              <>
                <X className="h-4 w-4" />
                Remover Foto
              </>
            )}
          </button>
        )}
      </div>

      {/* Informações de ajuda */}
      <div className="text-xs text-gray-500 text-center">
        <p>Formatos aceitos: JPG, PNG, WEBP, GIF</p>
        <p>Tamanho máximo: 5MB</p>
      </div>

      {/* Modal de Crop */}
      {showCropModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Ajustar Foto</h3>
              <p className="text-sm text-gray-600 mt-1">
                Arraste para posicionar e use o zoom para ajustar o tamanho
              </p>
            </div>
            
            <div 
              className="relative" 
              style={{ 
                height: '500px',
                width: '100%',
                background: '#1a1a1a',
                position: 'relative'
              }}
            >
              {imageToCrop ? (
                <div style={{ position: 'relative', width: '100%', height: '500px' }}>
                  <Cropper
                    image={imageToCrop}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="round"
                    minZoom={0.5}
                    maxZoom={3}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                  onCropComplete={(_, croppedAreaPixels) => {
                    if (croppedAreaPixels && !isNaN(croppedAreaPixels.width) && !isNaN(croppedAreaPixels.height)) {
                      setCroppedAreaPixels(croppedAreaPixels)
                    }
                  }}
                    showGrid={true}
                    restrictPosition={true}
                    style={{
                      containerStyle: {
                        position: 'relative',
                        width: '100%',
                        height: '500px',
                        background: '#1a1a1a'
                      },
                      cropAreaStyle: {
                        border: '3px dashed rgba(255, 255, 255, 0.9)',
                        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)'
                      },
                      mediaStyle: {
                        objectFit: 'contain'
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center" style={{ height: '500px' }}>
                  <div className="text-center text-white">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                    <p>Carregando imagem...</p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              {/* Controles de Zoom */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Zoom
                  </label>
                  <span className="text-sm text-gray-600">{zoom.toFixed(1)}x</span>
                </div>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1x</span>
                  <span>3x</span>
                </div>
              </div>

              {/* Dica */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  💡 <strong>Dica:</strong> Arraste a imagem para posicionar e ajuste o zoom para enquadrar perfeitamente
                </p>
              </div>

              {/* Botões de ação */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCancelCrop}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Cancelar
                </button>
                <button
                  onClick={handleCropComplete}
                  disabled={!croppedAreaPixels}
                  className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check className="h-4 w-4" />
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UploadFotoPerfil

